import type { MessageInfoSchemaType } from '..';
import {
  MessageWindowBoundary,
  type MessageWindowBoundaries,
  type MessageWindowBoundaryState,
  type MessageWindowSnapshot,
} from './state';

const createInitialBoundaries = (): MessageWindowBoundaries => ({
  [MessageWindowBoundary.NEWER]: { cursor: null, hasMore: false },
  [MessageWindowBoundary.OLDER]: { cursor: null, hasMore: true },
});

const compareMessages = (left: MessageInfoSchemaType, right: MessageInfoSchemaType): number => {
  const createdAtDifference: number = left.createdAt.getTime() - right.createdAt.getTime();
  if (createdAtDifference !== 0) {
    return createdAtDifference;
  }

  return left.id.localeCompare(right.id);
};

const createMessageWindow = () => {
  const messagesById = new Map<MessageInfoSchemaType['id'], MessageInfoSchemaType>();
  let boundaries: MessageWindowBoundaries = createInitialBoundaries();

  const snapshot = (): MessageWindowSnapshot => ({
    boundaries: {
      [MessageWindowBoundary.NEWER]: { ...boundaries[MessageWindowBoundary.NEWER] },
      [MessageWindowBoundary.OLDER]: { ...boundaries[MessageWindowBoundary.OLDER] },
    },
    messages: [...messagesById.values()].sort(compareMessages),
  });

  const upsertServer = (message: MessageInfoSchemaType): void => {
    messagesById.set(message.id, message);
  };

  const replaceServerWindow = (
    messages: MessageInfoSchemaType[],
    nextBoundaries: MessageWindowBoundaries,
  ): void => {
    messagesById.clear();
    messages.forEach(upsertServer);
    boundaries = {
      [MessageWindowBoundary.NEWER]: { ...nextBoundaries[MessageWindowBoundary.NEWER] },
      [MessageWindowBoundary.OLDER]: { ...nextBoundaries[MessageWindowBoundary.OLDER] },
    };
  };

  const merge = (
    boundary: MessageWindowBoundary,
    messages: MessageInfoSchemaType[],
    nextBoundary: MessageWindowBoundaryState,
  ): void => {
    messages.forEach(upsertServer);
    boundaries = { ...boundaries, [boundary]: { ...nextBoundary } };
  };

  const mergeOlder = (
    messages: MessageInfoSchemaType[],
    nextBoundary: MessageWindowBoundaryState,
  ): void => merge(MessageWindowBoundary.OLDER, messages, nextBoundary);

  const mergeNewer = (
    messages: MessageInfoSchemaType[],
    nextBoundary: MessageWindowBoundaryState,
  ): void => merge(MessageWindowBoundary.NEWER, messages, nextBoundary);

  const updateServer = (message: MessageInfoSchemaType): void => {
    if (messagesById.has(message.id)) {
      upsertServer(message);
    }
  };

  const removeServer = (messageId: MessageInfoSchemaType['id']): void => {
    messagesById.delete(messageId);
  };

  const hasMessage = (messageId: MessageInfoSchemaType['id']): boolean =>
    messagesById.has(messageId);

  return {
    hasMessage,
    mergeNewer,
    mergeOlder,
    removeServer,
    replaceServerWindow,
    snapshot,
    updateServer,
    upsertServer,
  };
};

export { createMessageWindow };
